import { DateTime } from 'luxon'

/*
 * The words of a list of days, shared by the booking flow's free times and the public timetable's
 * termíny on a phone (docs/public-booking.md, docs/events.md). Every day is a `yyyy-MM-dd` key, a
 * day on the premise's wall clock, never the browser's.
 */

const capitalize = (text: string) => text.charAt(0).toLocaleUpperCase('sk') + text.slice(1)

/** "Pondelok 5. októbra". */
export const dayLabel = (key: string): string =>
  capitalize(DateTime.fromISO(key).setLocale('sk').toFormat('cccc d. MMMM'))

/** "10. októbra". */
export const dateLabel = (key: string): string => DateTime.fromISO(key).setLocale('sk').toFormat('d. MMMM')

/** Weekdays in the genitive, which Luxon does not decline: "Od pondelka", "Od stredy". Luxon's 1 is Monday. */
const WEEKDAY_GENITIVE = ['', 'pondelka', 'utorka', 'stredy', 'štvrtka', 'piatka', 'soboty', 'nedele']

/** "Od pondelka 5. októbra", the list's header. */
export const fromLabel = (key: string): string => {
  const day = DateTime.fromISO(key).setLocale('sk')
  return `Od ${WEEKDAY_GENITIVE[day.weekday]} ${day.toFormat('d. MMMM')}`
}

/** Slovak plurals: 1 "termín", 2-4 "termíny", 0 and 5+ "termínov". */
export function termCountLabel(count: number): string {
  if (count === 1) return '1 termín'
  if (count >= 2 && count <= 4) return `${count} termíny`
  return `${count} termínov`
}

/**
 * The days from `fromKey` up to the day before `untilKey` have nothing: "10. októbra nie je žiadny termín.",
 * "10. – 11. októbra nie sú žiadne termíny.", "31. októbra – 1. novembra nie sú žiadne termíny.".
 */
export function noTermsLabel(fromKey: string, untilKey: string): string {
  const from = DateTime.fromISO(fromKey).setLocale('sk')
  const last = DateTime.fromISO(untilKey).setLocale('sk').minus({ days: 1 })
  if (last <= from) return `${dateLabel(fromKey)} nie je žiadny termín.`
  const start = from.hasSame(last, 'month') ? from.toFormat('d.') : from.toFormat('d. MMMM')
  return `${start} – ${last.toFormat('d. MMMM')} nie sú žiadne termíny.`
}

/** A day's items before or from noon, on the premise's wall clock. */
export interface DayPeriod<T> {
  key: 'morning' | 'afternoon'
  /** "Dopoludnia" / "Popoludní". */
  label: string
  items: T[]
}

/** Items split at 12:00 of their `HH:mm` (`timeOf`), in order; an empty half is left out. */
export function splitAtNoon<T>(items: readonly T[], timeOf: (item: T) => string): DayPeriod<T>[] {
  const periods: DayPeriod<T>[] = [
    { key: 'morning', label: 'Dopoludnia', items: items.filter((item) => timeOf(item) < '12:00') },
    { key: 'afternoon', label: 'Popoludní', items: items.filter((item) => timeOf(item) >= '12:00') },
  ]
  return periods.filter((period) => period.items.length)
}
